import type { PaginationMeta } from '@alpha/api-client';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';
import {
  EmptyState,
  Pagination,
  SpinnerBlock,
  Table,
  TableCard,
  TableHeader,
  TableWrap,
} from '../ui';

export interface DataTableProps<T> {
  title: string;
  // Column defs come from createColumnHelper, whose value type varies per column.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  data: T[] | undefined;
  meta?: PaginationMeta;
  isLoading?: boolean;
  error?: Error | null;
  emptyLabel?: string;
  /** Rendered on the right of the table header — tab filters, search, actions. */
  toolbar?: ReactNode;
  onPageChange?: (page: number) => void;
}

/**
 * TanStack Table in MANUAL mode: no client-side sorting, filtering or
 * pagination models are registered. The server owns all of it and hands us one
 * page at a time (§6.4).
 */
export function DataTable<T>({
  title,
  columns,
  data,
  meta,
  isLoading,
  error,
  emptyLabel = 'No records found',
  toolbar,
  onPageChange,
}: DataTableProps<T>) {
  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: meta?.last_page ?? -1,
  });

  return (
    <TableCard>
      <TableHeader
        title={title}
        meta={meta ? `${meta.total.toLocaleString('en-US')} total` : undefined}
        actions={toolbar}
      />

      {isLoading ? (
        <SpinnerBlock />
      ) : error ? (
        <EmptyState title="Could not load data" description={error.message} tone="danger" />
      ) : !data || data.length === 0 ? (
        <EmptyState title={emptyLabel} />
      ) : (
        <TableWrap>
          <Table>
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id}>
                  {group.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {meta && meta.last_page > 1 && onPageChange ? (
        <Pagination
          page={meta.current_page}
          lastPage={meta.last_page}
          total={meta.total}
          onChange={onPageChange}
        />
      ) : null}
    </TableCard>
  );
}
